import { Button } from "@spz/ui/components/button";
import { ButtonGroup } from "@spz/ui/components/button-group";
import { Typography } from "@spz/ui/components/typography";
import { cn } from "@spz/ui/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  ConclusionSelectField as ConclusionSelectFieldType,
  FieldValidationError,
  FieldValue,
} from "../../../../shared/types/workflow/workflow-form.types";
import { SelectField } from "./select-field";

// Type for conclusion items in ConclusionSelectField
interface ConclusionItem {
  id: string;
  labelKeyName?: string;
  descriptionKeyName?: string;
  name?: string;
}

export interface ConclusionSelectFieldProps {
  field: ConclusionSelectFieldType;
  value: FieldValue;
  error?: FieldValidationError;
  onChange: (value: FieldValue) => void;
}

export function ConclusionSelectField({
  field,
  value,
  error,
  onChange,
}: ConclusionSelectFieldProps) {
  const { t } = useTranslation();

  // Compute placeholder using field.name (as in original code)
  const placeholder = field.required ? `${t(field.name)}*` : t(field.name);

  // Initialize selected category with no selection
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >(undefined);

  // Filter conclusions based on selected category
  const selectedCategory = field.options?.find(
    (cat) => cat.id === selectedCategoryId,
  );

  const filteredOptions =
    selectedCategory?.conclusions?.map((opt: ConclusionItem) => ({
      value: opt.id,
      label: t(opt.labelKeyName ?? ""),
      description: t(opt.descriptionKeyName ?? ""),
    })) || [];

  // Handle category change
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);

    // Find the new category
    const newCategory = field.options?.find((cat) => cat.id === categoryId);

    // Check if current selected value exists in the new category's conclusions
    const currentValue = value as string | string[];
    const currentValueArray = Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : [];

    const newCategoryConclusions =
      newCategory?.conclusions?.map((c: ConclusionItem) => c.id) || [];

    // If current selection is not in the new category, clear it
    const isSelectionValid = currentValueArray.every((val) =>
      newCategoryConclusions.includes(val),
    );

    if (!isSelectionValid && currentValueArray.length > 0) {
      onChange(field.multiple ? [] : ("" as FieldValue));
    }
  };

  return (
    <div className="mb-4">
      <ButtonGroup className="mb-2" size="xl">
        {field.options?.map((category) => {
          const isActive = selectedCategoryId === category.id;
          return (
            <Button
              key={category.id}
              variant={isActive ? "primary" : "secondary"}
              className={cn(
                !isActive && "border-gray-200 text-blue-900",
                !isActive && selectedCategory && "bg-gray-50 text-gray-500",
              )}
              onClick={(e) => {
                e.preventDefault();
                handleCategoryChange(category.id);
              }}
              size="xl"
            >
              {t(category.labelKeyName ?? category.name)}
            </Button>
          );
        })}
      </ButtonGroup>
      <SelectField
        placeholder={placeholder}
        options={filteredOptions}
        value={value as string[]}
        multi={!!field.multiple}
        error={error}
        className="max-w-full" // to erase the max-w-md from the component
        popoverContentAlign="end"
        onSelect={(selected) => {
          if (field.multiple) {
            onChange(selected as unknown as FieldValue);
          } else {
            onChange((selected[0] ?? "") as FieldValue);
          }
        }}
      />
      {error && (
        <Typography
          className="mt-1 text-red-500"
          trans={error.translationKey}
          transParams={error.params}
        />
      )}
    </div>
  );
}
